'use client';

import React, { useMemo, useState } from 'react';
import { SegmentedControl, SegmentedControlItem } from '../../../core-components';
import { HeaderSection } from '../../shared/HeaderSection';
import { DashboardCardGrid } from '../DashboardCardGrid';
import type { PictogramName } from '../../../core-components';
import type { DashboardCardGridItem } from '../DashboardCardGrid';
import type { FC, ReactNode } from 'react';
export type CardGroupSectionCardProps = DashboardCardGridItem;
export type CardGroupSectionCategory<I = CardGroupSectionCardProps> = {
  headline?: string;
  subline?: ReactNode;
  items: I[];
};
export type CardGroupSectionProps = {
  /**
   * Section header (appears above all content)
   */
  headline?: string;
  headlineAs?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  subline?: ReactNode;
  pictogram?: PictogramName;
  centered?: boolean;
  /**
   * Uncategorized cards (displayed without tabs/categories)
   */
  uncategorizedItems?: CardGroupSectionCardProps[];
  /**
   * Categorized cards (displayed with tabs on mobile/tablet, sections on desktop)
   */
  categorizedItems?: CardGroupSectionCategory[];
  /**
   * Fallback/loading state
   */
  fallback?: boolean;
};
export const CardGroupSection: FC<CardGroupSectionProps> = ({
  headline,
  headlineAs = 'h2',
  subline,
  pictogram,
  centered,
  uncategorizedItems,
  categorizedItems,
  fallback
}) => {
  const hasHeader = headline || subline || pictogram;
  const [selectedSegment, setSelectedSegment] = useState<string>('0');
  const currentItems = useMemo(() => {
    if (!categorizedItems || categorizedItems.length === 0) {
      return [];
    }
    const categoryIndex = parseInt(selectedSegment, 10);
    return categorizedItems[categoryIndex]?.items || [];
  }, [selectedSegment, categorizedItems]);
  if (fallback) {
    return <div className="space-y-6">
        {hasHeader && <HeaderSection fallback centered={centered} />}
        <DashboardCardGrid fallback centered={centered} items={[]} className="gap-4 md:gap-6" />
      </div>;
  }
  const hasMultipleCategories = categorizedItems && categorizedItems.length > 1;
  return <div className="space-y-6" data-sentry-component="CardGroupSection" data-sentry-source-file="CardGroupSection.tsx">
      {hasHeader && <HeaderSection headline={headline} headlineAs={headlineAs} subline={subline} pictogram={pictogram} centered={centered} />}

      <div className="space-y-10">
        {categorizedItems && categorizedItems.length > 0 && <>
            {/* Mobile/Tablet: Tabs */}
            <div className="lg:hidden">
              <div className="flex flex-col gap-4 text-marine-high-emphasis">
                {hasMultipleCategories && <SegmentedControl value={selectedSegment} onValueChange={setSelectedSegment}>
                    {categorizedItems.map((category, idx) => <SegmentedControlItem key={idx} value={String(idx)} label={category.headline || ''} />)}
                  </SegmentedControl>}
                {renderItems({
              items: currentItems,
              centeredIfLessThanSpecified: centered
            })}
              </div>
            </div>

            {/* Desktop: Separate sections */}
            {categorizedItems.map((category, idx) => <div key={idx} className="hidden space-y-6 lg:block">
                <HeaderSection headline={category.headline} subline={category.subline} centered={centered} />
                {renderItems({
            items: category.items,
            centeredIfLessThanSpecified: centered
          })}
              </div>)}
          </>}

        {uncategorizedItems && uncategorizedItems.length > 0 && renderItems({
        items: uncategorizedItems,
        centeredIfLessThanSpecified: true
      })}
      </div>
    </div>;
};
const renderItems = (args: {
  items?: CardGroupSectionCardProps[];
  centeredIfLessThanSpecified?: boolean;
}) => {
  const {
    items,
    centeredIfLessThanSpecified: centered
  } = args;
  if (typeof items === 'undefined') {
    return null;
  }
  return <DashboardCardGrid items={items} centered={centered} className="gap-4 md:gap-6" data-sentry-element="DashboardCardGrid" data-sentry-component="renderItems" data-sentry-source-file="CardGroupSection.tsx" />;
};